// The Work chat composer session: the draft and its host sync, attachment
// staging and pre-upload, turn building (skills, routines, make-as-workflow,
// linked context), queueing, approval steer, the credit-allowance hold, the Esc
// stop, draft persistence, and the placeholder and aria copy.
//
// What the app owns crosses from the binding at the old path
// (`src/features/projects/hooks/useProjectsComposerChatSession.ts`): Chat's
// pre-upload lifecycle and the flag that turns it on, the linkable rows, the
// library documents, the routines and their skills, the shortcuts switch and the
// hotkey registry the Esc stop registers in, the credit-allowance refusal the
// banner reads, and the connected-app labels. The routines arrive as data: this
// module never reads the Routines zone.
import { toast } from '@heidi/design-system/toast';
import { useLatestRef } from '@heidi/utils';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useIntl } from 'react-intl';

import {
  composerUploadMetadataToBlocks,
  isSameLinkedContext,
  isSilentTurnAbandonment,
  mintTurnId,
  serializeLinkedContext,
  type ContextLock,
  type LinkedContextItem,
  type RpaSegmentedTask,
  type TurnOperation,
  type WorkflowSkillWithKind,
  type WorkSurface,
} from '../../../contracts';
import {
  buildProjectsComposerMessage,
  buildWorkflowTurnWireText,
  compileSegmentedTask,
  CompileSegmentedTaskError,
  COMPOSER_SEND_QUEUE_UX,
  composerDraftStorageKey,
  composerLinkedContext,
  composerLinkedItemsAtom,
  CONNECT_QUEUE_UX,
  deriveChatBusy,
  EMPTY_PROJECTS_COMPOSER_PROMPT_DRAFT,
  FOLLOW_UP_QUEUE_UX,
  getLastSentTurnToRestore,
  getProjectsComposerWorkflow,
  getSubmitTurnErrorMessage,
  hasProjectsComposerSkill,
  isCreditAllowanceExhausted,
  isProjectsComposerDraftEmpty,
  parseUserPreamble,
  PROJECT_CHAT_COMPOSER_PLACEHOLDER_VALUES,
  projectChatComposerMessages,
  projectRunFailureMessages,
  projectsComposerDraftDisplayText,
  projectsComposerDraftWireText,
  projectsComposerSubmitPrompts,
  ProvisionalSubmitRejectedError,
  resolveAvailableWorkflowSkill,
  resolveMakeAsWorkflowSubmit,
  setComposerLinkedItemAtom,
  useComposerDraftPersistence,
  useQueuedTurns,
  useRestoreDiscardedDraft,
  useSmartComposerFocus,
  type ComposerTurn,
  type DiscardableTurn,
  type LinkableContextItems,
  type ProjectChatEnqueueUx,
  type ProjectsComposerPromptDraft,
  type QueuedComposerTurn,
  type RestorableSentTurn,
  type SteerPendingApprovals,
  type SubmitProjectTurnArgs,
} from '../../../data';
import { injected } from '../../../data/injected-auth';
import { toastAttachmentStaging } from '../../composer/attachmentStagingFeedback';
import type { ComposerPromptEditorHandle } from '../../composer/Composer';
import {
  composerUploadedAttachmentMetadata,
  composerUploadedAttachments,
  type ComposerAttachmentItem,
  type ComposerAttachments,
  type ComposerUploadedFile,
} from '../../composer/composer-attachments';
import type { InlineClarificationBlockProps } from '../components/InlineClarificationBlock';
import { mergePickedFiles, PROJECT_ATTACHMENTS_ACCEPT_FORMATS } from '../lib/attachments';
import type { ProjectTransport } from '../transport/useProjectTransport';
import { useComposerApprovalSteer } from './useComposerApprovalSteer';

/** The Heidi Work verdict, resolved app-side (it ORs PostHog, which this package cannot read). */
const useIsHeidiWorkEnabled = (): boolean => injected('useIsHeidiWorkEnabled')();

/**
 * In-session chats always carry a host-session chip. That chip is already
 * the chat's scope — it is not extra payload a ``reason`` cannot hold.
 * Strip the ``@session`` wire so steering uses the clinician's words.
 */
const composerTurnForApprovalSteer = (turn: ComposerTurn): ComposerTurn => {
  const body = parseUserPreamble(turn.displayText ?? turn.text).body.trim();
  return {
    ...turn,
    text: body || turn.text,
    displayText: undefined,
    linkedItems: [],
  };
};

export interface ProjectsComposerSession {
  activeClarificationApproval: InlineClarificationBlockProps['approval'] | null;
  cancelling: boolean;
  cancelTurn: () => void;
  clearLinkedItems: () => void;
  hasPendingApproval: boolean;
  linkedItems: LinkedContextItem[];
  runClosed: boolean;
  /** Live-grant viewer: lock the composer without treating the run as closed. */
  readOnly?: boolean;
  runFailed: boolean;
  startTurn: ProjectTransport['startTurn'];
  approvalMode: ProjectTransport['approvalMode'];
  onApprovalModeChange: ProjectTransport['setApprovalMode'];
  turnInFlight: boolean;
  /** Compatibility bridge: remount hosts with a pending seed not yet adopted. */
  isStartingSeededTurn?: boolean;
  wrappingUp?: boolean;
  discardedTurnNonce?: number;
  discardedTurn?: DiscardableTurn | null;
  onHasQueuedTurnsChange?: (hasQueuedTurns: boolean) => void;
  onSteerPendingApprovals?: SteerPendingApprovals;
  projectId?: string;
  /** Backend-confirmed recording-child binding on the current Project run. */
  recordingEligible?: boolean;
  analyticsSurface?: WorkSurface;
  analyticsClinicalSessionId?: string;
  desktopTargetEnabled?: boolean;
  /** Host-owned draft persistence scope (suffix under the v1 namespace). */
  draftStorageScope?: string;
  /**
   * A draft the host holds across this composer's lifetime: it seeds the draft
   * on mount and hears every change. Work Home swaps its scene for this chat
   * when a patient is linked, and the typed text has to survive the trip.
   */
  hostDraft?: {
    get: () => ProjectsComposerPromptDraft;
    set: (draft: ProjectsComposerPromptDraft) => void;
  };
  onScrollTranscript?: () => void;
  /**
   * Physical context card lock; null when unrestricted. Non-null also means
   * the host session (or frozen ``data_scope``) cannot be dismissed or
   * swapped from the composer.
   */
  contextLock?: ContextLock | null;
}

export interface ProjectsComposerHandle {
  seedComposer: (prompt: string) => void;
  submitPrompt: (prompt: string, options?: { preserveDraft?: boolean }) => void;
  /** Send a shortcut as its own turn. The typed draft and staged files stay. */
  submitShortcut: (query: string) => void;
  continueAfterConnect: (connectorId: string) => void;
  stageFiles: (files: FileList | readonly File[] | null) => void;
  notifyTranscriptEngage: () => void;
  /** Parent publishers enqueue and receive a correlated operation. */
  enqueueTurn: (command: SubmitProjectTurnArgs) => TurnOperation;
  /** Seeded first turns promote to the head so they stay the opening command. */
  enqueueTurnNow: (command: SubmitProjectTurnArgs) => TurnOperation;
}

/** The editor calls this session makes; the composer's prompt editor handle has them. */
type ComposerSessionEditor = Pick<
  ComposerPromptEditorHandle,
  'focus' | 'replaceDraft' | 'replaceText' | 'clear'
>;

/** What the composer that runs this session passes in. */
export interface ProjectsComposerChatSessionArgs extends ProjectsComposerSession {
  isSubmitting: boolean;
  editorRef: RefObject<ComposerSessionEditor | null>;
  /** Run-state placeholder copy wins over this idle override. */
  idlePlaceholder?: string;
  /** Sendable payload emptiness — text/skills/attachments, not the payload itself. */
  onComposerEmptyChange?: (empty: boolean) => void;
  /** Visible-only idle preview (Evidence starter hover); never names the textbox. */
  idlePlaceholderPreview?: string | null;
  /**
   * A locked chat may still take the host patient's other sessions as extra
   * context. Only set when the pickers are scoped to that patient; a session
   * outside this scope is rejected even if something else offers it.
   */
  lockedSessionPatientScope?: {
    patientProfileId: string;
    excludeSessionId: string;
  } | null;
}

/** One pre-uploaded file, as the session reads it. */
export interface ComposerChatUploadFile extends ComposerUploadedFile {
  file: File;
}

/**
 * Chat's pre-upload lifecycle. The app runs it over the Work upload adapter, with
 * its own error copy, and hands its state and commands in.
 */
export interface ComposerChatUploads<TFile extends ComposerChatUploadFile> {
  files: readonly TFile[];
  addFiles: (files: File[]) => void;
  removeFile: (uid: string) => void;
  clearFiles: (deleteFromServer?: boolean) => void;
  retryUpload: (uid: string) => void;
  hasErrors: boolean;
}

/**
 * The credit-allowance refusal the app keeps for the banner above the composer:
 * a refused send's text is kept as its ghost, nudged when a follow-up is held,
 * and cleared once a send is accepted. One object; pass a stable one.
 */
export interface CreditAllowanceRefusalCallbacks {
  remember: (refusal: { projectId: string; ghostText: string }) => void;
  clear: (projectId: string) => void;
  nudge: (projectId: string) => void;
  shouldSkipFollowUpSend: (projectId: string) => boolean;
}

/** A routine a mid-chat turn can run: the name its bubble shows and the brief it sends. */
export interface ComposerRoutine {
  workflow_id: string;
  name: string;
  instructions: string;
}

/**
 * The page-wide Esc stop. A hook, because it registers in the app's hotkey
 * scopes; pass a stable one.
 */
export type UseStopGeneratingHotkey = (onStop: () => void, enabled: boolean) => void;

/** What the app resolves for the session. */
export interface ProjectsComposerChatSessionAppInputs<
  TFile extends ComposerChatUploadFile,
> {
  /** Chat's pre-upload flag, read at render; off stages files in the browser. */
  attachmentsEnabled: boolean;
  uploads: ComposerChatUploads<TFile>;
  /** The pickers' recent sessions and patients. */
  linkableContextItems: Pick<LinkableContextItems, 'sessionItems' | 'patientItems'>;
  /** The locked patient's other sessions; empty without a patient scope. */
  lockedPatientSessionItems: LinkableContextItems['sessionItems'];
  referenceableLibraryDocuments: readonly LinkedContextItem[];
  availableSkills: readonly WorkflowSkillWithKind[];
  /** The clinician's routines, for a routine chip in the draft. */
  routines: readonly ComposerRoutine[] | undefined;
  /** The app's switch for keyboard shortcuts; it gates the page-wide Esc stop. */
  isShortcutsDisabled: boolean;
  useStopGeneratingHotkey: UseStopGeneratingHotkey;
  creditAllowanceRefusal: CreditAllowanceRefusalCallbacks;
  /** The display name of a connected app, by connector id. */
  resolveConnectorLabel: (connectorId: string) => string;
}

export const useProjectsComposerChatSessionCore = <TFile extends ComposerChatUploadFile>({
  cancelling,
  cancelTurn,
  clearLinkedItems,
  hasPendingApproval,
  isSubmitting,
  linkedItems,
  runClosed,
  readOnly = false,
  runFailed,
  startTurn,
  turnInFlight,
  isStartingSeededTurn = false,
  wrappingUp = false,
  discardedTurnNonce,
  discardedTurn = null,
  onHasQueuedTurnsChange,
  onSteerPendingApprovals,
  projectId,
  draftStorageScope: hostDraftStorageScope,
  hostDraft,
  onScrollTranscript,
  contextLock = null,
  editorRef,
  idlePlaceholder,
  onComposerEmptyChange,
  idlePlaceholderPreview,
  lockedSessionPatientScope = null,
  analyticsSurface,
  analyticsClinicalSessionId,
  attachmentsEnabled,
  uploads,
  linkableContextItems,
  lockedPatientSessionItems,
  referenceableLibraryDocuments: libraryDocuments,
  availableSkills,
  routines,
  isShortcutsDisabled,
  useStopGeneratingHotkey,
  creditAllowanceRefusal,
  resolveConnectorLabel,
}: ProjectsComposerChatSessionArgs & ProjectsComposerChatSessionAppInputs<TFile>) => {
  // Single source of truth: a lock object IS the locked state, so a
  // half-locked composer (locked behaviour, no card) is unrepresentable.
  const intl = useIntl();
  const contextLocked = contextLock != null;
  const [draft, setDraft] = useState<ProjectsComposerPromptDraft>(
    () => hostDraft?.get() ?? EMPTY_PROJECTS_COMPOSER_PROMPT_DRAFT
  );
  const setHostDraft = hostDraft?.set;
  useEffect(() => {
    setHostDraft?.(draft);
  }, [draft, setHostDraft]);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const usePreUploadAttachments = attachmentsEnabled;
  const {
    files: preUploadedFiles,
    addFiles: addPreUploadedFiles,
    removeFile: removePreUploadedFile,
    clearFiles: clearPreUploadedFiles,
    retryUpload: retryPreUploadedFile,
    hasErrors: hasAttachmentUploadErrors,
  } = uploads;
  const { items: uploadedItems, readyToSend: uploadsReadyToSend } = useMemo(
    () => composerUploadedAttachments(preUploadedFiles),
    [preUploadedFiles]
  );
  const attachmentItems = useMemo<readonly ComposerAttachmentItem[]>(
    () =>
      usePreUploadAttachments
        ? uploadedItems
        : stagedFiles.map((file, index) => ({
            id: `${file.name}-${file.size}-${index}`,
            name: file.name,
            type: file.type,
            size: file.size,
            status: 'staged' as const,
            source: { kind: 'file' as const, file },
          })),
    [stagedFiles, uploadedItems, usePreUploadAttachments]
  );
  const stagedAttachmentCount = usePreUploadAttachments
    ? preUploadedFiles.length
    : stagedFiles.length;
  const attachmentsNotReadyForSend = usePreUploadAttachments && !uploadsReadyToSend;
  const [disableComposerForApprovalSteer, setDisableComposerForApprovalSteer] =
    useState(false);
  const [sendSucceeded, setSendSucceeded] = useState(false);
  const lastSentTurnRef = useRef<DiscardableTurn | null>(null);
  const sendFlashTimeoutRef = useRef<number | null>(null);

  const setLinkedItem = useSetAtom(setComposerLinkedItemAtom);
  const setLinkedItems = useSetAtom(composerLinkedItemsAtom);
  const { sessionItems, patientItems } = linkableContextItems;
  const linkableItems = useMemo(
    () => [...sessionItems, ...patientItems, ...libraryDocuments],
    [libraryDocuments, patientItems, sessionItems]
  );
  const isWorkflowsEnabled = useIsHeidiWorkEnabled();

  const draftAndFilesAreEmpty =
    isProjectsComposerDraftEmpty(draft) && stagedAttachmentCount === 0;
  const composerIsEmptyRef = useLatestRef(
    draftAndFilesAreEmpty && linkedItems.length === 0
  );

  useEffect(() => {
    onComposerEmptyChange?.(draftAndFilesAreEmpty);
  }, [draftAndFilesAreEmpty, onComposerEmptyChange]);
  useEffect(() => () => onComposerEmptyChange?.(true), [onComposerEmptyChange]);

  const removeStagedFile = useCallback(
    (index: number) => {
      if (usePreUploadAttachments) {
        const file = preUploadedFiles[index];
        if (file) removePreUploadedFile(file.uid);
        return;
      }
      setStagedFiles((prev) => prev.filter((_, i) => i !== index));
    },
    [preUploadedFiles, removePreUploadedFile, usePreUploadAttachments]
  );

  const focusComposerRaw = useCallback(() => {
    queueMicrotask(() => editorRef.current?.focus('end'));
  }, [editorRef]);

  const {
    onEditorFocus: trackEditorFocus,
    onEditorBlur: trackEditorBlur,
    onTranscriptEngage,
    focusComposerSmart,
    focusComposerAlways,
  } = useSmartComposerFocus({ focus: focusComposerRaw });

  const restoreTurnToComposer = useCallback(
    (turn: RestorableSentTurn, focus: 'smart' | 'always') => {
      editorRef.current?.replaceDraft(turn.draft);
      setDraft(turn.draft);
      setStagedFiles(turn.files ? [...turn.files] : []);
      setLinkedItem(turn.linkedItems?.[0] ?? null);
      if (focus === 'always') focusComposerAlways();
      else focusComposerSmart();
    },
    [editorRef, focusComposerAlways, focusComposerSmart, setLinkedItem]
  );

  const draftStorageKey = hostDraftStorageScope
    ? composerDraftStorageKey(hostDraftStorageScope)
    : projectId
      ? composerDraftStorageKey(projectId)
      : null;
  const { clearPersistedDraft } = useComposerDraftPersistence({
    storageKey: draftStorageKey ?? composerDraftStorageKey('disabled'),
    enabled: Boolean(draftStorageKey) && !runClosed && !readOnly,
    draft,
    linkedItems,
    linkableItems,
    editorRef,
    onRestoreLinkedItem: setLinkedItem,
    onDraftRestored: setDraft,
  });

  const flashSendSuccess = useCallback(() => {
    setSendSucceeded(true);
    if (sendFlashTimeoutRef.current !== null) {
      window.clearTimeout(sendFlashTimeoutRef.current);
    }
    sendFlashTimeoutRef.current = window.setTimeout(() => {
      setSendSucceeded(false);
      sendFlashTimeoutRef.current = null;
    }, 650);
  }, []);

  useEffect(
    () => () => {
      if (sendFlashTimeoutRef.current !== null) {
        window.clearTimeout(sendFlashTimeoutRef.current);
      }
    },
    []
  );

  const onFilesPicked = useCallback(
    (eventFiles: FileList | readonly File[] | null) => {
      const existingFiles = usePreUploadAttachments
        ? preUploadedFiles.map((file) => file.file)
        : stagedFiles;
      const previousCount = existingFiles.length;
      const { next, error } = mergePickedFiles(existingFiles, eventFiles);
      const addedCount = next.length - previousCount;
      if (error && addedCount <= 0) toast({ ...error, tone: 'destructive' });
      if (usePreUploadAttachments) {
        const added = next.slice(previousCount);
        if (added.length > 0) addPreUploadedFiles(added);
      } else {
        setStagedFiles(next);
      }
      if (!usePreUploadAttachments) {
        toastAttachmentStaging(intl, previousCount, next.length, Boolean(error));
      }
      focusComposerAlways();
    },
    [
      addPreUploadedFiles,
      focusComposerAlways,
      intl,
      preUploadedFiles,
      stagedFiles,
      usePreUploadAttachments,
    ]
  );

  const attachments = useMemo<ComposerAttachments>(
    () => ({
      items: attachmentItems,
      attach: onFilesPicked,
      remove: (id) => {
        const index = attachmentItems.findIndex((item) => item.id === id);
        if (index >= 0) removeStagedFile(index);
      },
      retry: retryPreUploadedFile,
      readyToSend: !attachmentsNotReadyForSend,
      accept: PROJECT_ATTACHMENTS_ACCEPT_FORMATS,
    }),
    [
      attachmentItems,
      attachmentsNotReadyForSend,
      onFilesPicked,
      removeStagedFile,
      retryPreUploadedFile,
    ]
  );

  const buildTurn = useCallback(
    (currentDraft: ProjectsComposerPromptDraft = draft): ComposerTurn | null => {
      const draftHasSkill = hasProjectsComposerSkill(currentDraft);
      const draftEmpty = isProjectsComposerDraftEmpty(currentDraft);
      if (draftEmpty && stagedAttachmentCount === 0) return null;

      if (attachmentsNotReadyForSend) {
        if (hasAttachmentUploadErrors) {
          toast({
            title: intl.formatMessage({
              defaultMessage: "Couldn't send with failed attachments",
              id: 'BdpRaZG43T',
            }),
            description: intl.formatMessage({
              defaultMessage: 'Remove or retry the failed files, then send again.',
              id: 'eLO05hViyX',
            }),
            tone: 'destructive',
          });
        } else {
          toast({
            title: intl.formatMessage({
              defaultMessage: 'Uploading attachments…',
              id: 'exWXlWlMBv',
            }),
            description: intl.formatMessage({
              defaultMessage: 'Wait for uploads to finish before sending.',
              id: 'gijxfqMM8w',
            }),
          });
        }
        return null;
      }

      const uploadedMetadata = usePreUploadAttachments
        ? composerUploadedAttachmentMetadata(uploadedItems)
        : [];
      const attachmentIds = uploadedMetadata.map((file) => file.attachment_id);
      const optimisticAttachments =
        uploadedMetadata.length > 0
          ? composerUploadMetadataToBlocks(uploadedMetadata)
          : undefined;
      const stagedFilesForTurn =
        !attachmentsEnabled && stagedFiles.length > 0 ? stagedFiles : undefined;

      // Minted before the turn can be queued, so a waiting message reaches the
      // backend under the identity its bubble already has.
      const turnId = mintTurnId();

      const workflowRef = getProjectsComposerWorkflow(currentDraft);
      const isWorkflow = Boolean(workflowRef);
      let computerUseSkill: RpaSegmentedTask | undefined;
      if (draftHasSkill && !isWorkflow) {
        try {
          computerUseSkill = compileSegmentedTask(
            currentDraft,
            resolveAvailableWorkflowSkill(availableSkills)
          );
        } catch (error) {
          const message =
            error instanceof CompileSegmentedTaskError
              ? error.message
              : 'Selected skill is unavailable';
          toast({
            title: "Couldn't use selected skill",
            description: message,
            tone: 'destructive',
          });
          return null;
        }
      }

      const linkedContextPreamble = serializeLinkedContext(
        composerLinkedContext(currentDraft, linkedItems)
      );

      const turnBase = {
        files: stagedFilesForTurn,
        attachment_ids: attachmentIds.length > 0 ? attachmentIds : undefined,
        optimisticAttachments,
        computerUseSkill,
        ...(analyticsSurface
          ? {
              analytics: {
                surface: analyticsSurface,
                ...(analyticsClinicalSessionId
                  ? { clinical_session_id: analyticsClinicalSessionId }
                  : {}),
              },
            }
          : {}),
        draft: currentDraft,
        linkedItems,
        turnId,
        failureUxOwner: 'composer',
      } satisfies Partial<ComposerTurn>;

      const makeAsWorkflow = resolveMakeAsWorkflowSubmit(
        projectsComposerDraftDisplayText(currentDraft) ?? ''
      );
      if (makeAsWorkflow && !draftHasSkill && !workflowRef) {
        const displayText = buildProjectsComposerMessage({
          linkedContextPreamble,
          text: makeAsWorkflow.display,
        });
        const wireText = buildProjectsComposerMessage({
          linkedContextPreamble,
          text: makeAsWorkflow.wire,
        });
        return {
          ...turnBase,
          text: wireText,
          displayText,
          previewText: displayText,
        };
      }

      if (workflowRef) {
        const workflow = routines?.find((r) => r.workflow_id === workflowRef.skillId);
        if (!workflow) {
          toast({
            title: intl.formatMessage({
              defaultMessage: 'Routine unavailable',
              id: 'Ovheo3FBjX',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'The selected routine could not be found. It may have been deleted.',
              id: 'FgrjFGZ/HU',
            }),
            tone: 'destructive',
          });
          return null;
        }
        const displayText = projectsComposerDraftDisplayText(currentDraft) ?? '';
        const wireText = buildWorkflowTurnWireText({
          instructions: workflow.instructions,
          additionalText: projectsComposerDraftWireText(currentDraft.segments),
          linkedContextPreamble,
        });
        return {
          ...turnBase,
          text: wireText,
          routineName: workflow.name,
          displayText: displayText !== wireText ? displayText : undefined,
          previewText: displayText,
        };
      }

      const { wireText, displayText } = projectsComposerSubmitPrompts({
        draft: currentDraft,
        linkedContextPreamble,
        computerUseSkill,
      });

      return {
        ...turnBase,
        text: wireText,
        displayText: displayText !== wireText ? displayText : undefined,
        previewText: displayText,
      };
    },
    [
      attachmentsEnabled,
      availableSkills,
      draft,
      attachmentsNotReadyForSend,
      analyticsSurface,
      analyticsClinicalSessionId,
      hasAttachmentUploadErrors,
      intl,
      linkedItems,
      stagedAttachmentCount,
      stagedFiles,
      routines,
      uploadedItems,
      usePreUploadAttachments,
    ]
  );

  const clearComposer = useCallback(
    (options?: { refocus?: boolean }) => {
      editorRef.current?.clear();
      setDraft(EMPTY_PROJECTS_COMPOSER_PROMPT_DRAFT);
      setStagedFiles([]);
      if (usePreUploadAttachments) clearPreUploadedFiles(false);
      if (!contextLocked && linkedItems.length > 0) clearLinkedItems();
      // Extra sessions are per message; the pinned host stays.
      if (contextLocked && linkedItems.length > 1) {
        setLinkedItems((current) => current.slice(0, 1));
      }
      clearPersistedDraft();
      if (options?.refocus !== false) {
        focusComposerSmart();
      }
    },
    [
      clearLinkedItems,
      clearPersistedDraft,
      clearPreUploadedFiles,
      contextLocked,
      editorRef,
      focusComposerSmart,
      linkedItems.length,
      setLinkedItems,
      usePreUploadAttachments,
    ]
  );

  const rememberLastSentTurn = useCallback((turn: ComposerTurn) => {
    lastSentTurnRef.current = {
      draft: turn.draft,
      files: turn.files,
      linkedItems: turn.linkedItems,
    };
  }, []);

  /** One "waiting to send" toast per busy queue burst — don't stack on every enqueue. */
  const queuedWaitingToastShownRef = useRef(false);

  const handleTurnSendFailure = useCallback(
    (turn: RestorableSentTurn, error: unknown, ghostText?: string) => {
      if (error instanceof ProvisionalSubmitRejectedError) {
        if (error.restoreDraft && composerIsEmptyRef.current) {
          restoreTurnToComposer(turn, 'smart');
        }
        return;
      }
      if (isSilentTurnAbandonment(error)) {
        return;
      }
      if (isCreditAllowanceExhausted(error)) {
        if (projectId) {
          creditAllowanceRefusal.remember({
            projectId,
            ghostText: ghostText ?? projectsComposerDraftDisplayText(turn.draft) ?? '',
          });
        }
        return;
      }
      // Never over a draft: a delayed failure re-enables the box long before
      // it lands, so the user may have retyped.
      if (composerIsEmptyRef.current) {
        restoreTurnToComposer(turn, 'smart');
      }
      toast({
        title: "Couldn't send message",
        description: getSubmitTurnErrorMessage(error, intl),
        tone: 'destructive',
      });
    },
    [composerIsEmptyRef, creditAllowanceRefusal, intl, projectId, restoreTurnToComposer]
  );

  const onOwnedTurnAccepted = useCallback(() => {
    if (projectId) creditAllowanceRefusal.clear(projectId);
    flashSendSuccess();
  }, [creditAllowanceRefusal, flashSendSuccess, projectId]);

  const onOwnedTurnRejected = useCallback(
    (turn: ComposerTurn, error: unknown) => {
      // Seeded / recording / note-push publishers own their own restore+toast.
      // Local command field only — never analytics.surface.
      if ((turn.failureUxOwner ?? 'composer') !== 'composer') return;
      handleTurnSendFailure(
        { draft: turn.draft, files: turn.files, linkedItems: turn.linkedItems },
        error,
        turn.previewText
      );
    },
    [handleTurnSendFailure]
  );

  const {
    queuedTurns,
    isCommandBusy,
    handleSendNow,
    removeQueuedTurn,
    editQueuedTurn,
    setQueuedEditing,
    clearQueuedTurns,
    sendTurnNow,
    sendOrQueueTurn,
    enqueueTurn,
    enqueueTurnNow,
  } = useQueuedTurns({
    cancelTurn,
    clearComposer: (options) => {
      clearComposer(options);
      // The box IS empty now; the ref only catches up on the next render.
      composerIsEmptyRef.current = true;
    },
    draft,
    isStartingSeededTurn,
    isSubmitting,
    onHasQueuedTurnsChange,
    onScrollTranscript,
    rememberLastSentTurn,
    startTurn,
    turnInFlight,
    onTurnAccepted: onOwnedTurnAccepted,
    onTurnRejected: onOwnedTurnRejected,
    onTurnQueued: (_turn, ux) => {
      if (!ux.toast || queuedWaitingToastShownRef.current) return;
      queuedWaitingToastShownRef.current = true;
      toast({
        title: intl.formatMessage({
          defaultMessage: 'Message waiting to send',
          id: 'H3BjLs8gDt',
        }),
        description: intl.formatMessage({
          defaultMessage: 'Will send when Heidi finishes the current turn.',
          id: '3GwIPKLTKv',
        }),
      });
    },
  });

  useEffect(() => {
    if (queuedTurns.length === 0) {
      queuedWaitingToastShownRef.current = false;
    }
  }, [queuedTurns.length]);

  const linkedContextIsOwnedByDiscardedTurn =
    linkedItems.length === 1 &&
    isSameLinkedContext(linkedItems[0], discardedTurn?.linkedItems?.[0]);
  const composerIsPristine =
    draftAndFilesAreEmpty &&
    queuedTurns.length === 0 &&
    (linkedItems.length === 0 || linkedContextIsOwnedByDiscardedTurn);

  useRestoreDiscardedDraft({
    discardedTurnNonce,
    discardedTurn,
    canRestore: composerIsPristine,
    onRestore: (turn) => restoreTurnToComposer(turn, 'always'),
  });

  const steerPendingApproval = useComposerApprovalSteer({
    clearComposer,
    flashSendSuccess,
    handleSendNow: sendTurnNow,
    intl,
    onScrollTranscript,
    onSteerPendingApprovals,
    rememberLastSentTurn,
    sendOrQueueTurn,
    setDisableComposerForApprovalSteer,
  });

  const handleQueuedSendNow = useCallback(
    (queued: QueuedComposerTurn) => {
      if (hasPendingApproval) {
        removeQueuedTurn(queued.id);
        void steerPendingApproval(
          contextLocked ? composerTurnForApprovalSteer(queued.turn) : queued.turn
        );
        return;
      }
      handleSendNow(queued);
    },
    [
      contextLocked,
      handleSendNow,
      hasPendingApproval,
      removeQueuedTurn,
      steerPendingApproval,
    ]
  );

  // Must come before the queue test: a pending approval reports as an
  // in-flight turn, but the queue drains on `turnInFlight → false`,
  // which cannot happen until the approval resolves — so queueing here
  // strands the message.
  const ingestTurn = useCallback(
    (turn: ComposerTurn, ux: ProjectChatEnqueueUx) => {
      if (hasPendingApproval) {
        void steerPendingApproval(
          contextLocked ? composerTurnForApprovalSteer(turn) : turn,
          ux
        );
        return;
      }
      if (projectId && creditAllowanceRefusal.shouldSkipFollowUpSend(projectId)) {
        creditAllowanceRefusal.remember({
          projectId,
          ghostText: projectsComposerDraftDisplayText(turn.draft) ?? '',
        });
        if (ux.clearComposer) clearComposer();
        creditAllowanceRefusal.nudge(projectId);
        return;
      }
      sendOrQueueTurn(turn, ux);
    },
    [
      clearComposer,
      contextLocked,
      creditAllowanceRefusal,
      hasPendingApproval,
      projectId,
      sendOrQueueTurn,
      steerPendingApproval,
    ]
  );

  const submitDraft = useCallback(
    (currentDraft: ProjectsComposerPromptDraft = draft) => {
      if (attachmentsNotReadyForSend) return;
      const turn = buildTurn(currentDraft);
      if (!turn) return;
      ingestTurn(turn, COMPOSER_SEND_QUEUE_UX);
    },
    [attachmentsNotReadyForSend, buildTurn, draft, ingestTurn]
  );

  const isDraftEmpty = draftAndFilesAreEmpty;
  const isChatBusy =
    isCommandBusy ||
    deriveChatBusy({
      turnInFlight,
      isSubmitting,
      hasQueuedTurns: queuedTurns.length > 0,
    });
  const showStop =
    isDraftEmpty &&
    !runClosed &&
    !readOnly &&
    !hasPendingApproval &&
    (isChatBusy || cancelling);
  const isWrappingUp = showStop && wrappingUp && !cancelling && !isSubmitting;

  const handleUnhandledEditorKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const restoredTurn = getLastSentTurnToRestore({
        key: event.key,
        draftEmpty: isProjectsComposerDraftEmpty(draft),
        stagedFileCount: stagedAttachmentCount,
        lastSentTurn: lastSentTurnRef.current,
      });
      if (restoredTurn) {
        event.preventDefault();
        restoreTurnToComposer(restoredTurn, 'always');
        return true;
      }
      if (event.key !== 'Escape') return false;
      if (!showStop || cancelling || isSubmitting) return false;
      event.preventDefault();
      cancelTurn();
      return true;
    },
    [
      cancelTurn,
      cancelling,
      draft,
      isSubmitting,
      restoreTurnToComposer,
      showStop,
      stagedAttachmentCount,
    ]
  );

  // The editor handler above only sees Esc while the composer has focus; this
  // catches it anywhere else on the page. A surface that closes on Esc must
  // claim it (capture-phase preventDefault, as Radix does) or this also fires;
  // the app's hotkey registration already ignores keys typed into a field.
  useStopGeneratingHotkey(
    cancelTurn,
    showStop && !cancelling && !isSubmitting && !isShortcutsDisabled
  );

  const seedComposer = useCallback(
    (prompt: string) => {
      editorRef.current?.replaceText(prompt);
      queueMicrotask(() => {
        editorRef.current?.focus('end');
      });
    },
    [editorRef]
  );

  const submitPrompt = useCallback(
    (prompt: string, options?: { preserveDraft?: boolean }) => {
      const trimmed = prompt.trim();
      if (!trimmed) return;
      const makeAsWorkflow = resolveMakeAsWorkflowSubmit(trimmed);
      const visibleText = makeAsWorkflow?.display ?? trimmed;
      if (
        !options?.preserveDraft &&
        (!isProjectsComposerDraftEmpty(draft) || stagedAttachmentCount > 0)
      ) {
        seedComposer(visibleText);
        return;
      }
      const turn: ComposerTurn = {
        text: makeAsWorkflow?.wire ?? trimmed,
        displayText: makeAsWorkflow?.display,
        draft: { text: visibleText, segments: [{ kind: 'text', text: visibleText }] },
        previewText: visibleText,
        linkedItems: [],
        turnId: mintTurnId(),
        failureUxOwner: 'composer',
      };
      ingestTurn(turn, FOLLOW_UP_QUEUE_UX);
    },
    [draft, ingestTurn, seedComposer, stagedAttachmentCount]
  );

  const submitShortcut = useCallback(
    (query: string) => {
      const trimmed = query.trim();
      if (!trimmed) return;
      const shortcutDraft: ProjectsComposerPromptDraft = {
        text: trimmed,
        segments: [{ kind: 'text', text: trimmed }],
      };
      const linkedContextPreamble = serializeLinkedContext(
        composerLinkedContext(shortcutDraft, linkedItems)
      );
      const { wireText, displayText } = projectsComposerSubmitPrompts({
        draft: shortcutDraft,
        linkedContextPreamble,
      });
      const turn: ComposerTurn = {
        text: wireText,
        displayText: displayText !== wireText ? displayText : undefined,
        draft: shortcutDraft,
        previewText: displayText,
        linkedItems,
        turnId: mintTurnId(),
        failureUxOwner: 'composer',
      };
      ingestTurn(turn, FOLLOW_UP_QUEUE_UX);
    },
    [ingestTurn, linkedItems]
  );

  const handleLinkContextSet = useCallback(
    (item: LinkedContextItem | null) => {
      if (contextLocked) {
        // The host stays pinned at [0]; another session of the patient rides after it.
        if (!lockedSessionPatientScope || item?.type !== 'session') return;
        const isPatientSession = lockedPatientSessionItems.some(
          (candidate) =>
            candidate.type === 'session' && candidate.sessionId === item.sessionId
        );
        if (!isPatientSession) return;
        setLinkedItems((current) =>
          current.length === 0 ||
          current.some(
            (existing) =>
              existing.type === 'session' && existing.sessionId === item.sessionId
          )
            ? current
            : [...current, item]
        );
        focusComposerAlways();
        return;
      }
      setLinkedItem(item);
      if (item) focusComposerAlways();
    },
    [
      contextLocked,
      lockedPatientSessionItems,
      lockedSessionPatientScope,
      focusComposerAlways,
      setLinkedItem,
      setLinkedItems,
    ]
  );

  const continueAfterConnect = useCallback(
    (connectorId: string) => {
      const label = resolveConnectorLabel(connectorId);
      const wireText =
        `I've connected ${label}. Please continue with the previous request ` +
        'and retry any connected-apps tools that needed this connection.';
      const displayText = `Connected ${label}`;
      const continueDraft: ProjectsComposerPromptDraft = {
        text: wireText,
        segments: [{ kind: 'text', text: wireText }],
      };
      const turn: ComposerTurn = {
        text: wireText,
        displayText,
        draft: continueDraft,
        previewText: displayText,
        linkedItems,
        turnId: mintTurnId(),
        failureUxOwner: 'composer',
      };
      ingestTurn(turn, CONNECT_QUEUE_UX);
    },
    [ingestTurn, linkedItems, resolveConnectorLabel]
  );

  const handle: ProjectsComposerHandle = useMemo(
    () => ({
      seedComposer,
      submitPrompt,
      submitShortcut,
      continueAfterConnect,
      stageFiles: onFilesPicked,
      notifyTranscriptEngage: onTranscriptEngage,
      enqueueTurn,
      enqueueTurnNow,
    }),
    [
      continueAfterConnect,
      enqueueTurn,
      enqueueTurnNow,
      onFilesPicked,
      onTranscriptEngage,
      seedComposer,
      submitPrompt,
      submitShortcut,
    ]
  );

  // `null` means "idle" — the one state an idle override or its hover preview
  // may speak in.
  const runStatePlaceholder = runFailed
    ? intl.formatMessage(projectRunFailureMessages.composerPlaceholder)
    : readOnly
      ? intl.formatMessage({
          defaultMessage: 'This thread is view only.',
          id: 'VKs89UMFi+',
          description:
            'Composer placeholder when the current user can only view the thread',
        })
      : runClosed
        ? 'This project run is closed.'
        : cancelling
          ? intl.formatMessage({
              defaultMessage: 'Stopping current turn…',
              id: 'Y1iQnThvph',
            })
          : turnInFlight
            ? hasPendingApproval
              ? intl.formatMessage(
                  projectRunFailureMessages.composerClarificationReplyPlaceholder
                )
              : intl.formatMessage(projectRunFailureMessages.composerQueuePlaceholder)
            : null;

  const isDefaultIdlePlaceholder =
    runStatePlaceholder === null && idlePlaceholder === undefined;
  const placeholder =
    runStatePlaceholder ??
    idlePlaceholder ??
    intl.formatMessage(
      projectChatComposerMessages.placeholder,
      PROJECT_CHAT_COMPOSER_PLACEHOLDER_VALUES
    );

  // The preview draws as an overlay rather than through Tiptap's placeholder, so
  // it no longer inherits "hidden once the editor has content" for free — a
  // typed prompt has to keep the line to itself.
  const placeholderPreview =
    runStatePlaceholder || !isProjectsComposerDraftEmpty(draft)
      ? null
      : (idlePlaceholderPreview ?? null);

  const stopAriaLabel = cancelling
    ? intl.formatMessage({
        defaultMessage: 'Stopping turn',
        id: 'gMDk5Ng51u',
      })
    : isWrappingUp
      ? intl.formatMessage(projectRunFailureMessages.stopHeidi)
      : intl.formatMessage({
          defaultMessage: 'Stop generating',
          id: 'GV5peKDvCU',
        });

  const sendAriaLabel = hasPendingApproval
    ? intl.formatMessage({
        defaultMessage: 'Send answer',
        id: '+MERBHFirm',
        description: 'Work chat send button while answering a clarification',
      })
    : isChatBusy
      ? intl.formatMessage({
          defaultMessage: 'Queue message',
          id: 'KjiIZ10sTq',
          description: 'Work chat send button while a turn is in flight',
        })
      : intl.formatMessage({
          defaultMessage: 'Send message',
          id: 'LNdf3jvNBS',
          description: 'Work chat send button',
        });

  return {
    draft,
    setDraft,
    preUploadedFiles,
    attachments,
    onFilesPicked,
    queuedTurns,
    handleSendNow: handleQueuedSendNow,
    removeQueuedTurn,
    editQueuedTurn,
    setQueuedEditing,
    clearQueuedTurns,
    submitDraft,
    handleUnhandledEditorKeyDown,
    showStop,
    isWrappingUp,
    sendSucceeded,
    disableComposerForApprovalSteer,
    handleEditorFocus: trackEditorFocus,
    handleEditorBlur: trackEditorBlur,
    handleLinkContextSet,
    focusComposerAlways,
    availableSkills,
    isWorkflowsEnabled,
    handle,
    placeholder,
    isDefaultIdlePlaceholder,
    placeholderPreview,
    sendAriaLabel,
    stopAriaLabel,
  };
};

/** What the session hands the composer that runs it. */
export type ProjectsComposerChatSessionState<
  TFile extends ComposerChatUploadFile = ComposerChatUploadFile,
> = ReturnType<typeof useProjectsComposerChatSessionCore<TFile>>;
